import { getIdToken } from "../auth/useAuth";
import {
  ApiError,
  type ApiErrorBody,
  type AutocompleteResponse,
  type MortgageRate,
  type ProfilePreferences,
  type PropertySummary,
  type PropertySummaryResponse,
  type PutProfileRequest,
  type PutProfileResponse,
  type ResolvedPlace,
  type ResolvePlaceResponse,
  type ScoreResponse,
  type TiltMap,
  type UsageStatus,
} from "../types/api";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";
const CHAT_URL = import.meta.env.VITE_CHAT_URL || "";
const REPORT_URL = import.meta.env.VITE_REPORT_URL || "";
const NARRATIVE_URL = import.meta.env.VITE_NARRATIVE_URL || "";

/**
 * Task 4.0a — every call to the v1.0 API surface carries a Cognito
 * ID token. The token is fetched lazily from Amplify per call so it's
 * always fresh; Amplify rotates the cached token before expiry. If the
 * user isn't authenticated, the header is omitted and the backend will
 * respond with a 401 the handler surfaces to the UI.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function parseError(res: Response): Promise<ApiError> {
  let code = "http_error";
  let message = `Request failed with status ${res.status}`;
  let extra: Omit<ApiErrorBody, "error"> | undefined;
  try {
    const body = (await res.json()) as ApiErrorBody;
    if (body?.error) {
      code = body.error.code || code;
      message = body.error.message || message;
    }
    // Entitlement denials (403/429) carry a usage object + upgrade context so
    // the UI can render the right CTA / reset date (Req 14.2).
    const rest = { ...(body ?? {}) } as Partial<ApiErrorBody>;
    delete rest.error;
    if (Object.keys(rest).length > 0) extra = rest as Omit<ApiErrorBody, "error">;
  } catch {
    // Body wasn't JSON; keep the defaults.
  }
  return new ApiError(res.status, code, message, extra);
}

export interface PostScoreOptions {
  place_id?: string | null;
  /** Spend the free-tier one-time Pro trial on this property (Req 11.3). */
  is_trial_action?: boolean;
  /** Force a fresh report even if a recent one exists (Req 2.5). */
  force_refresh?: boolean;
}

export async function postScore(
  address: string,
  asking_price: number,
  opts: PostScoreOptions | string | null = {},
): Promise<ScoreResponse> {
  // Back-compat: earlier callers passed place_id as the 3rd positional arg.
  const options: PostScoreOptions =
    typeof opts === "string" || opts === null ? { place_id: opts } : opts;
  // Req 16 — forward the autocomplete-selected place_id when available so
  // the backend doesn't need to re-resolve it. Omitted entirely (not sent
  // as null/empty) when the user typed the address manually; the backend
  // falls back to SearchPlaceIndexForText in that case.
  const body: Record<string, unknown> = { address, asking_price };
  if (options.place_id) body.place_id = options.place_id;
  if (options.is_trial_action) body.is_trial_action = true;
  if (options.force_refresh) body.force_refresh = true;
  const res = await fetch(`${API_BASE}/score`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as ScoreResponse;
}

/**
 * Current entitlement usage for the signed-in user (usage-tracking-limits
 * Req 15). Powers the account-settings usage line + low-quota toast. Open to
 * both free and paid tiers.
 */
export async function getUsage(): Promise<UsageStatus> {
  const res = await fetch(`${API_BASE}/usage`, {
    method: "GET",
    headers: await authHeaders(),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as UsageStatus;
}

export async function getScore(
  scoreId: string,
  includeChat = false,
): Promise<ScoreResponse> {
  const qs = includeChat ? "?include_chat=true" : "";
  const res = await fetch(`${API_BASE}/score/${scoreId}${qs}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as ScoreResponse;
}

/**
 * Pattern C tier-poll endpoint (v1.0 Task 6.12 / Req 20.5b–5d).
 *
 * Called every ~3s after Stripe Checkout completes. Returns the LIVE
 * Cognito tier attribute, not the JWT claim — so the upgrade is visible
 * before the client refreshes its ID token. Cache-Control: no-store on
 * the backend prevents CDN short-circuiting.
 */
export interface UserTierResponse {
  tier: "free" | "paid";
  checked_at: string;
}

export async function getUserTier(): Promise<UserTierResponse> {
  const res = await fetch(`${API_BASE}/user/tier`, {
    method: "GET",
    headers: await authHeaders(),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as UserTierResponse;
}

// ---- Profile preferences (chat-personalization Task 10.1) -------------------

/**
 * Fetch the user's profile preferences (display name, search requirements,
 * target areas) for populating the Account page form. Returns null fields for
 * attributes the user hasn't set yet.
 */
export async function getProfile(): Promise<ProfilePreferences> {
  const res = await fetch(`${API_BASE}/user/profile`, {
    method: "GET",
    headers: await authHeaders(),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as ProfilePreferences;
}

/**
 * Save profile preferences. Invalidates the chat personalization cache so the
 * next chat session rebuilds the profile with the updated preferences.
 */
export async function putProfile(data: PutProfileRequest): Promise<PutProfileResponse> {
  const res = await fetch(`${API_BASE}/user/profile`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as PutProfileResponse;
}

export async function postAutocomplete(
  query: string,
): Promise<AutocompleteResponse> {
  const res = await fetch(`${API_BASE}/autocomplete`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as AutocompleteResponse;
}

/**
 * Resolve a place_id to coordinates (Task 9.2 / Req 5.8). The free-tier
 * flow has a place_id from autocomplete but needs lat/lon to render the
 * At-A-Glance map. Reuses the /autocomplete Lambda in "resolve" mode.
 */
export async function resolvePlace(placeId: string): Promise<ResolvedPlace> {
  const res = await fetch(`${API_BASE}/autocomplete`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ place_id: placeId }),
  });
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as ResolvePlaceResponse).place;
}

/**
 * Current average 30-yr mortgage rate (Task 9.7a cost-to-own). Server-fetched
 * from FRED with caching + fallback, so this always returns a number.
 */
export async function getMortgageRate(): Promise<MortgageRate> {
  const res = await fetch(`${API_BASE}/mortgage-rate`, {
    method: "GET",
    headers: await authHeaders(),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as MortgageRate;
}

/**
 * Free-tier property summary card data (Task 9.9a). Web search + LLM
 * extraction on the backend (~2-4s), so the card renders a skeleton first
 * and fills when this resolves. Returns all-null fields on backend failure.
 */
export async function getPropertySummary(
  placeId: string,
  address: string,
): Promise<PropertySummary> {
  const res = await fetch(`${API_BASE}/property/summary`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ place_id: placeId, address }),
  });
  if (!res.ok) throw await parseError(res);
  return ((await res.json()) as PropertySummaryResponse).summary;
}

// ---- Exterior photo / Street View (curb-photo) -----------------------------

export interface PropertyPhoto {
  available: boolean;
  /** Deep-link to Google's own Street View — present only when available. */
  streetview_url?: string;
  /** Embeddable Street View Static image URL — only returned when `size` is
   *  passed (Saved-page thumbnail; that page has no geographic map). */
  image_url?: string;
  photo_date?: string | null;
}

/**
 * Street View coverage for a property (curb-photo). Returns a link to Google's
 * own Street View when imagery exists, else `{ available: false }` (also on any
 * backend failure). The route is public.
 *
 * The property-detail pages call WITHOUT a size and link out — embedding a
 * Google image on the same screen as our Amazon Location map would violate
 * Google's "No Use With Non-Google Maps" term, so no image/key reaches them.
 * The Saved page (no geographic map on-screen) passes a `size` and additionally
 * gets an `image_url` it can embed as a thumbnail, with a "© Google" credit.
 *
 * Never throws on a "no photo" outcome — only a transport/HTTP error rejects,
 * which callers treat the same as unavailable.
 */
export async function getPropertyPhoto(
  placeId: string,
  size?: string,
): Promise<PropertyPhoto> {
  const qs = size ? `?size=${encodeURIComponent(size)}` : "";
  const res = await fetch(
    `${API_BASE}/property/${encodeURIComponent(placeId)}/photo${qs}`,
    { headers: await authHeaders() },
  );
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as PropertyPhoto;
}

// ---- Saved properties (Task 9.4) -------------------------------------------

export interface SavedProperty {
  place_id: string;
  address: string | null;
  baseline_avm: number | null;
  asking_price: number | null;
  saved_at: string;
  // Latest score's Tilt Map (quadrant + plot coords); null until scored under
  // tiltmap-v1. Drives the Saved-board map.
  tilt_map: TiltMap | null;
}

export async function getUserSaved(): Promise<{ saved: SavedProperty[]; count: number }> {
  const res = await fetch(`${API_BASE}/user/saved`, { headers: await authHeaders() });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as { saved: SavedProperty[]; count: number };
}

/**
 * Recompute a prior score at a new asking price with zero external calls — the
 * Tilt Map dot moves (and the offer range updates) live as the buyer edits the
 * price. Server-side so the X/Y thresholds never reach the browser. Throws a
 * 404 facts_unavailable for scores that predate facts archival (caller keeps
 * the static dot).
 */
export async function repriceScore(
  scoreId: string,
  askingPrice: number,
): Promise<ScoreResponse> {
  const res = await fetch(`${API_BASE}/score/${encodeURIComponent(scoreId)}/reprice`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ asking_price: askingPrice }),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as ScoreResponse;
}

export async function saveProperty(
  placeId: string,
  address: string,
  baselineAvm?: number | null,
  askingPrice?: number | null,
): Promise<void> {
  const res = await fetch(`${API_BASE}/user/saved/${encodeURIComponent(placeId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({
      address,
      baseline_avm: baselineAvm ?? undefined,
      asking_price: askingPrice ?? undefined,
    }),
  });
  if (!res.ok) throw await parseError(res);
}

export async function deleteSavedProperty(placeId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/user/saved/${encodeURIComponent(placeId)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  });
  if (!res.ok) throw await parseError(res);
}

export async function setSavedMute(placeId: string, muted: boolean): Promise<void> {
  const res = await fetch(`${API_BASE}/user/saved/${encodeURIComponent(placeId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ muted }),
  });
  if (!res.ok) throw await parseError(res);
}

// ---- Multi-property compare (Task 9.5) -------------------------------------

export interface CompareFactor {
  name: string;
  factor_key: string;
  direction: string;
  direction_label: string;
  summary: string;
}

export interface CompareProperty {
  place_id: string;
  score_id: string;
  address: string;
  asking_price: number;
  tilt_map: {
    quadrant: string;
    recommendation: string;
    x_position: number | null;
    y_position: number | null;
  } | null;
  offer_range: { low_pct: number; high_pct: number; low_price: number; high_price: number };
  factors: CompareFactor[];
  scored_at: string;
  is_stale: boolean;
  strongest_factor_key: string | null;
  weakest_factor_key: string | null;
}

export async function getCompare(placeIds: string[]): Promise<{ properties: CompareProperty[] }> {
  const qs = encodeURIComponent(placeIds.join(","));
  const res = await fetch(`${API_BASE}/compare?place_ids=${qs}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as { properties: CompareProperty[] };
}

// ---- Alerts (Task 9.6) -----------------------------------------------------

export interface AlertItem {
  alert_id: string;
  alert_type: string;
  place_id: string;
  address: string | null;
  // Deep-links the inbox row to the shared Tilt Report (#36/#37). Absent on
  // legacy alerts written before score_id was persisted.
  score_id: string | null;
  detected_at: string;
  summary: string;
}

export async function getAlerts(): Promise<{
  alerts: AlertItem[];
  count: number;
  globally_muted: boolean;
}> {
  const res = await fetch(`${API_BASE}/alerts`, { headers: await authHeaders() });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as {
    alerts: AlertItem[];
    count: number;
    globally_muted: boolean;
  };
}

export async function setGlobalAlertMute(muted: boolean): Promise<void> {
  const res = await fetch(`${API_BASE}/alerts/mute`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ muted }),
  });
  if (!res.ok) throw await parseError(res);
}

// ---- Stripe Checkout (Task 9.3) --------------------------------------------

export async function postCheckout(): Promise<{ checkout_url: string }> {
  const res = await fetch(`${API_BASE}/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as { checkout_url: string };
}

export async function postBillingPortal(): Promise<{ portal_url: string }> {
  const res = await fetch(`${API_BASE}/billing-portal`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as { portal_url: string };
}

export async function deleteAccount(): Promise<{ deleted: boolean }> {
  const res = await fetch(`${API_BASE}/account`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as { deleted: boolean };
}

// ---- Chat streaming ---------------------------------------------------------

export type ChatStreamEvent =
  | { event: "token"; data: string }
  | { event: "tool_call"; data: { tool: string; args_summary?: string } }
  | { event: "revised_score"; data: unknown }
  | { event: "system_note"; data: { code?: string; message: string } }
  | { event: "done" }
  | { event: "error"; data: { message: string; code?: string } };

/**
 * Chat target — Task 9.0. Paid users chat about a verified score
 * (scoreId); free users chat about a property (placeId, no score).
 */
export type ChatTarget = { scoreId: string } | { placeId: string };

/**
 * Stream an auto-greeting from the /chat Function URL (chat-personalization
 * Task 11.1). POSTs { score_id, greeting: true } with NO content field.
 * Yields the same SSE events as streamChat. If the stream returns only a
 * `done` event (no tokens), treat as "no greeting available." On any network
 * or fetch error the generator throws — caller must catch and fall back.
 */
export async function* streamGreeting(
  scoreId: string,
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamEvent, void, unknown> {
  if (!CHAT_URL) {
    throw new ApiError(500, "configuration", "VITE_CHAT_URL not set.");
  }

  const res = await fetch(CHAT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ score_id: scoreId, greeting: true }),
    signal,
  });
  if (!res.ok || !res.body) throw await parseError(res);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const parsed = parseSseBlock(block);
      if (parsed) yield parsed;
      if (parsed?.event === "done") return;
    }
  }
}

/**
 * Stream chat events from the /chat Function URL.
 *
 * Yields parsed SSE-style events as they arrive. Caller can iterate with
 * `for await (const ev of streamChat(...))`. AbortSignal cancels the stream.
 */
export async function* streamChat(
  target: ChatTarget,
  content: string,
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamEvent, void, unknown> {
  if (!CHAT_URL) {
    throw new ApiError(500, "configuration", "VITE_CHAT_URL not set.");
  }

  const body: Record<string, unknown> = { content };
  if ("scoreId" in target) body.score_id = target.scoreId;
  else body.place_id = target.placeId;

  const res = await fetch(CHAT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) throw await parseError(res);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Process complete SSE blocks separated by \n\n
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const parsed = parseSseBlock(block);
      if (parsed) yield parsed;
      if (parsed?.event === "done") return;
    }
  }
}

// ---- Narrative streaming (pro-ai-analyst Task 7/10) ------------------------

export type NarrativeStreamEvent =
  | { event: "investigation_step"; data: { step: string } }
  | { event: "token"; data: string }
  | { event: "done"; data: { suggested_questions?: string[]; disclaimer?: string } }
  | { event: "error"; data: { message: string; code?: string } };

/**
 * Stream the Pro AI Analyst's opening brief from the narrative Function URL.
 *
 * POSTs {score_id} and yields parsed SSE events: investigation steps as the
 * analyst works, then the brief text, then a `done` carrying property-specific
 * suggested follow-up questions. Same buffered-SSE contract as streamChat.
 *
 * `urlOverride` is the `narrative_url` returned on the score response; it wins
 * over the build-time VITE_NARRATIVE_URL fallback.
 */
export async function* streamNarrative(
  scoreId: string,
  signal?: AbortSignal,
  urlOverride?: string,
): AsyncGenerator<NarrativeStreamEvent, void, unknown> {
  const url = urlOverride || NARRATIVE_URL;
  if (!url) {
    throw new ApiError(500, "configuration", "VITE_NARRATIVE_URL not set.");
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ score_id: scoreId }),
    signal,
  });
  if (!res.ok || !res.body) throw await parseError(res);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const parsed = parseNarrativeSseBlock(block);
      if (parsed) yield parsed;
      if (parsed?.event === "done") return;
    }
  }
}

function parseNarrativeSseBlock(block: string): NarrativeStreamEvent | null {
  let event = "";
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      const raw = line.slice(5);
      dataLines.push(raw.startsWith(" ") ? raw.slice(1) : raw);
    }
  }
  const data = dataLines.join("\n");
  switch (event) {
    case "investigation_step":
      try {
        return { event: "investigation_step", data: JSON.parse(data) };
      } catch {
        return { event: "investigation_step", data: { step: data } };
      }
    case "token": {
      // Token payloads are JSON-encoded so embedded newlines survive the frame
      // boundary (same as streamChat).
      try {
        const parsed = JSON.parse(data);
        return { event: "token", data: typeof parsed === "string" ? parsed : data };
      } catch {
        return { event: "token", data };
      }
    }
    case "done":
      try {
        return { event: "done", data: JSON.parse(data) };
      } catch {
        return { event: "done", data: {} };
      }
    case "error":
      try {
        return { event: "error", data: JSON.parse(data) };
      } catch {
        return { event: "error", data: { message: data || "Unknown error" } };
      }
    default:
      return null;
  }
}

// ---- Report streaming (v1.0 Task 4.10) -------------------------------------

/** Names that match the 6-section template emitted by api/report_handler.py. */
export type ReportSectionName =
  | "at_a_glance"
  | "negotiation_levers"
  | "offer_strategy"
  | "offer_letter_framing"
  | "outstanding_items"
  | "citations";

export interface AtAGlanceContent {
  normalized_address: string;
  place_id: string;
  lat: number;
  lon: number;
  state_code: string;
  county_fips: string;
  asking_price: number | null;
}

export interface Citation {
  source: string;
  url?: string;
}

export type ReportStreamEvent =
  | { event: "section"; data: { section: "at_a_glance"; content: AtAGlanceContent } }
  | { event: "section"; data: { section: "citations"; content: Citation[] } }
  | { event: "section"; data: { section: Exclude<ReportSectionName, "at_a_glance" | "citations">; content: string } }
  | { event: "done" }
  | { event: "error"; data: { code?: string; message: string; upgrade_context?: unknown } };

/**
 * Stream report events from the /report Function URL.
 *
 * Sends Bearer token in Authorization header (Task 4.8a JWT validation —
 * auth_middleware.validate_jwt decodes/verifies against Cognito JWKS).
 * Same SSE-block parsing as streamChat. Yields parsed section/error/done
 * events as they arrive.
 */
export async function* streamReport(
  address: string,
  askingPrice: number | null,
  signal?: AbortSignal,
): AsyncGenerator<ReportStreamEvent, void, unknown> {
  if (!REPORT_URL) {
    throw new ApiError(500, "configuration", "VITE_REPORT_URL not set.");
  }
  const headers = await authHeaders();
  if (!headers.Authorization) {
    throw new ApiError(401, "missing_token", "Sign in to view a report.");
  }

  const res = await fetch(REPORT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify({
      address,
      asking_price: askingPrice ?? undefined,
    }),
    signal,
  });
  if (!res.ok || !res.body) throw await parseError(res);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const parsed = parseReportSseBlock(block);
      if (parsed) yield parsed;
      if (parsed?.event === "done") return;
    }
  }
}

function parseReportSseBlock(block: string): ReportStreamEvent | null {
  let event = "";
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      const raw = line.slice(5);
      dataLines.push(raw.startsWith(" ") ? raw.slice(1) : raw);
    }
  }
  const data = dataLines.join("\n");
  switch (event) {
    case "section":
      try {
        const parsed = JSON.parse(data) as { section: ReportSectionName; content: unknown };
        return { event: "section", data: parsed } as ReportStreamEvent;
      } catch {
        return null;
      }
    case "done":
      return { event: "done" };
    case "error":
      try {
        return { event: "error", data: JSON.parse(data) };
      } catch {
        return { event: "error", data: { message: data || "Unknown error" } };
      }
    default:
      return null;
  }
}

function parseSseBlock(block: string): ChatStreamEvent | null {
  let event = "";
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      // SSE spec: a single U+0020 SPACE after `data:` is ignored if present,
      // but any further whitespace IS part of the data. We previously called
      // .trim() which destroyed inter-token spacing (" ATTOM" became "ATTOM",
      // so the chat rendered as "TheATTOM" instead of "The ATTOM").
      const raw = line.slice(5);
      dataLines.push(raw.startsWith(" ") ? raw.slice(1) : raw);
    }
  }
  const data = dataLines.join("\n");
  switch (event) {
    case "token": {
      // Token payloads are JSON-encoded by the backend so embedded newlines
      // survive the SSE frame boundary. Fall back to raw for safety across a
      // deploy window where an older backend might still send plain text.
      try {
        const parsed = JSON.parse(data);
        return { event: "token", data: typeof parsed === "string" ? parsed : data };
      } catch {
        return { event: "token", data };
      }
    }
    case "tool_call":
      try {
        return { event: "tool_call", data: JSON.parse(data) };
      } catch {
        return { event: "tool_call", data: { tool: data } };
      }
    case "revised_score":
      try {
        return { event: "revised_score", data: JSON.parse(data) };
      } catch {
        return null;
      }
    case "system_note":
      try {
        return { event: "system_note", data: JSON.parse(data) };
      } catch {
        return { event: "system_note", data: { message: data } };
      }
    case "done":
      return { event: "done" };
    case "error":
      try {
        return { event: "error", data: JSON.parse(data) };
      } catch {
        return { event: "error", data: { message: data || "Unknown error" } };
      }
    default:
      return null;
  }
}
